import { Link } from 'react-router-dom';
import { Building2, CheckCircle, Trash2, Truck, Shield } from 'lucide-react';

function StoreDecommissioning() {
  return (
    <div className="pt-28">
      {/* Hero Section */}
      <section className="relative h-96 flex items-center justify-center overflow-hidden">
        <div 
          className="absolute inset-0 bg-cover bg-center"
          style={{ backgroundImage: 'url(/images/store_decommissioning_collage.jpg)' }}
        ></div>
        <div className="absolute inset-0 bg-black/50"></div>
        <div className="absolute inset-0 brass-lighting"></div>
        <div className="absolute inset-0 geometric-brass"></div>
        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h1 className="text-5xl md:text-6xl font-bold text-white mb-4 font-serif" style={{ fontFamily: 'Playfair Display, Georgia, serif' }}>
            Store <span className="text-gold">Decommissioning</span>
          </h1>
          <p className="text-xl text-gray-200 max-w-3xl mx-auto">
            Professional facility closure, asset recovery, and site restoration services
          </p>
        </div>
      </section>

      {/* Main Content */}
      <section className="bg-black py-20 relative overflow-hidden">
        <div className="absolute inset-0 brass-lighting"></div>
        <div className="absolute inset-0 geometric-brass"></div>
        <div className="absolute inset-0 shimmer-wave"></div>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="grid md:grid-cols-2 gap-16 items-start">
            <div>
              <h2 className="text-4xl font-bold text-white mb-6 font-serif" style={{ fontFamily: 'Playfair Display, Georgia, serif' }}>
                Complete Decommissioning <span className="text-gold">Solutions</span>
              </h2>
              <p className="text-gray-300 text-lg leading-relaxed mb-6">
                When retail locations, restaurants, or commercial facilities reach the end of their lease or operational lifecycle, Williams Diversified LLC delivers comprehensive decommissioning services that protect your brand, recover assets, and restore facilities to their original condition.
              </p>
              <p className="text-gray-300 text-lg leading-relaxed mb-6">
                Our experienced teams handle every aspect of the decommissioning process—from interior demolition and asset removal to final site restoration—ensuring landlord compliance and minimizing your financial exposure.
              </p>
              <p className="text-gray-300 text-lg leading-relaxed">
                We work efficiently to meet tight timelines, coordinate with property management, and deliver complete documentation of all work performed.
              </p>
            </div>

            <div>
              <div className="bg-gradient-to-br from-gold/10 to-black border border-gold/30 rounded-lg p-8">
                <h3 className="text-2xl font-bold text-white mb-6 font-serif" style={{ fontFamily: 'Playfair Display, Georgia, serif' }}>
                  Our <span className="text-gold">Process</span>
                </h3>
                <div className="space-y-4">
                  <div className="flex items-start space-x-3">
                    <CheckCircle className="w-6 h-6 text-gold flex-shrink-0 mt-1" />
                    <div>
                      <h4 className="text-white font-semibold mb-1">Asset Inventory & Recovery</h4>
                      <p className="text-gray-400 text-sm">Cataloging and secure removal of equipment, fixtures, and salvageable materials</p>
                    </div>
                  </div>
                  <div className="flex items-start space-x-3">
                    <CheckCircle className="w-6 h-6 text-gold flex-shrink-0 mt-1" />
                    <div>
                      <h4 className="text-white font-semibold mb-1">Interior Demolition</h4>
                      <p className="text-gray-400 text-sm">Safe removal of walls, finishes, fixtures, and built-in elements</p>
                    </div>
                  </div>
                  <div className="flex items-start space-x-3">
                    <CheckCircle className="w-6 h-6 text-gold flex-shrink-0 mt-1" />
                    <div>
                      <h4 className="text-white font-semibold mb-1">Utility Disconnection & Capping</h4>
                      <p className="text-gray-400 text-sm">Professional disconnection of electrical, plumbing, HVAC, and other systems</p>
                    </div>
                  </div>
                  <div className="flex items-start space-x-3">
                    <CheckCircle className="w-6 h-6 text-gold flex-shrink-0 mt-1" />
                    <div>
                      <h4 className="text-white font-semibold mb-1">Debris Removal & Site Cleaning</h4>
                      <p className="text-gray-400 text-sm">Complete removal of all materials and thorough cleaning of the space</p>
                    </div>
                  </div>
                  <div className="flex items-start space-x-3">
                    <CheckCircle className="w-6 h-6 text-gold flex-shrink-0 mt-1" />
                    <div>
                      <h4 className="text-white font-semibold mb-1">Restoration to Shell Condition</h4>
                      <p className="text-gray-400 text-sm">Returning the space to its pre-tenant condition per lease requirements</p>
                    </div>
                  </div>
                  <div className="flex items-start space-x-3">
                    <CheckCircle className="w-6 h-6 text-gold flex-shrink-0 mt-1" />
                    <div>
                      <h4 className="text-white font-semibold mb-1">Final Documentation & Inspection</h4>
                      <p className="text-gray-400 text-sm">Comprehensive photo documentation and coordination with landlord inspections</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Services Grid */}
      <section className="bg-black py-20 relative overflow-hidden border-t border-gold/30">
        <div className="absolute inset-0 brass-lighting"></div>
        <div className="absolute inset-0 brass-particles"></div>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-white mb-4 font-serif" style={{ fontFamily: 'Playfair Display, Georgia, serif' }}>
              What We <span className="text-gold">Handle</span>
            </h2>
            <p className="text-gray-300 text-lg max-w-3xl mx-auto">
              Complete decommissioning services for all commercial facility types
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            <div className="bg-black border border-gold/20 rounded-lg p-6 hover:border-gold transition-all group">
              <div className="text-gold mb-4 group-hover:scale-110 transition-transform">
                <Building2 className="w-12 h-12" />
              </div>
              <h3 className="text-xl font-bold text-white mb-3">Retail Stores</h3>
              <p className="text-gray-400">Complete closure services for retail locations of all sizes</p>
            </div>

            <div className="bg-black border border-gold/20 rounded-lg p-6 hover:border-gold transition-all group">
              <div className="text-gold mb-4 group-hover:scale-110 transition-transform">
                <Trash2 className="w-12 h-12" />
              </div>
              <h3 className="text-xl font-bold text-white mb-3">Restaurants</h3>
              <p className="text-gray-400">Kitchen equipment removal and grease trap decommissioning</p>
            </div>

            <div className="bg-black border border-gold/20 rounded-lg p-6 hover:border-gold transition-all group">
              <div className="text-gold mb-4 group-hover:scale-110 transition-transform">
                <Truck className="w-12 h-12" />
              </div>
              <h3 className="text-xl font-bold text-white mb-3">Office Spaces</h3>
              <p className="text-gray-400">Furniture removal, partition demolition, and restoration</p>
            </div>

            <div className="bg-black border border-gold/20 rounded-lg p-6 hover:border-gold transition-all group">
              <div className="text-gold mb-4 group-hover:scale-110 transition-transform">
                <Shield className="w-12 h-12" />
              </div>
              <h3 className="text-xl font-bold text-white mb-3">Multi-Location</h3>
              <p className="text-gray-400">Coordinated decommissioning across multiple sites nationwide</p>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="bg-black py-20 relative overflow-hidden border-t border-gold/30">
        <div className="absolute inset-0 brass-lighting"></div>
        <div className="absolute inset-0 shimmer-wave"></div>
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center relative z-10">
          <h2 className="text-4xl font-bold text-white mb-6 font-serif" style={{ fontFamily: 'Playfair Display, Georgia, serif' }}>
            Ready to Plan Your <span className="text-gold">Decommissioning Project</span>?
          </h2>
          <p className="text-gray-300 text-xl mb-10">
            Contact us today for a consultation and detailed proposal
          </p>
          <Link 
            to="/contact"
            className="inline-block bg-gold hover:bg-gold-dark text-black font-bold py-4 px-10 rounded text-lg transition-all transform hover:scale-105 uppercase tracking-wider"
          >
            Contact Us
          </Link>
        </div>
      </section>
    </div>
  );
}

export default StoreDecommissioning;